
import React from 'react';
import { Construction } from 'lucide-react';

const LegacyRegistration: React.FC = () => {
  const LabelInput = ({ label, required, className = "", defaultValue = "" }: { label: string, required?: boolean, className?: string, defaultValue?: string }) => (
    <div className={`flex flex-col mb-2 ${className}`}>
      <label className="text-[11px] font-sans text-gray-700 mb-0.5 whitespace-nowrap">
        {required && <span className="text-red-600 font-bold mr-0.5">*</span>}
        {label}:
      </label>
      <input 
        type="text" 
        defaultValue={defaultValue}
        className="border border-gray-400 bg-white px-1 py-0.5 text-[12px] h-6 focus:outline-none focus:border-blue-500 shadow-inner"
      />
    </div>
  );

  return (
    <div className="h-full bg-[#d4d0c8] overflow-auto flex flex-col p-1 relative">
      {/* Coming Soon Overlay */}
      <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-white px-8 py-6 rounded-2xl shadow-xl border border-slate-200 flex flex-col items-center">
          <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4">
            <Construction className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Coming Soon</h2>
          <p className="text-slate-500 text-center max-w-xs text-sm">
            We're currently fine-tuning this workflow. Check back later!
          </p>
        </div>
      </div>

      {/* Title Bar Simulation */}
      <div className="bg-gradient-to-r from-[#0054e3] to-[#27c4ff] text-white px-2 py-1 flex justify-between items-center text-sm font-bold shadow-md rounded-t-sm">
        <div className="flex items-center space-x-2">
          <i className="fa-solid fa-hospital text-xs"></i>
          <span>Register Outpatient</span>
        </div>
        <div className="flex space-x-1">
          <div className="w-4 h-4 bg-[#e5e5e5] border border-gray-400 text-black flex items-center justify-center text-[10px] leading-none">_</div>
          <div className="w-4 h-4 bg-[#e5e5e5] border border-gray-400 text-black flex items-center justify-center text-[10px] leading-none">□</div>
          <div className="w-4 h-4 bg-red-600 border border-red-800 text-white flex items-center justify-center text-[10px] leading-none">×</div>
        </div>
      </div>

      <div className="border-x border-b border-gray-400 bg-[#f0f0f0] p-3 flex-1 flex flex-col">
        {/* Header Section */}
        <div className="grid grid-cols-6 gap-3 mb-4 p-2 border border-gray-300 bg-white rounded-sm shadow-sm">
          <LabelInput label="Medical Record Number" defaultValue="" />
          <LabelInput label="Encounter Number" defaultValue="" />
          <LabelInput label="Last Name" required defaultValue="" />
          <LabelInput label="First Name" required defaultValue="" />
          <LabelInput label="Middle Name" defaultValue="" />
          <LabelInput label="Preferred Name" defaultValue="" />
          
          <LabelInput label="Previous Last Name" defaultValue="" />
          <LabelInput label="Maiden Name" defaultValue="" />
          <LabelInput label="Date of Birth" required defaultValue="" />
          <LabelInput label="Age" defaultValue="" />
          <LabelInput label="Gender" required defaultValue="" />
          <LabelInput label="BC PHN" defaultValue="" />
        </div>

        {/* Tab Strip */}
        <div className="flex border-b border-gray-400">
          {['ALERTS', 'Patient Information', 'Encounter Information', 'Insurance', 'Insurance Summary', 'Additional Contacts'].map((tab, idx) => (
            <div 
              key={tab} 
              className={`px-3 py-1 text-[11px] border-t border-x border-gray-400 mr-0.5 rounded-t-md cursor-pointer transition-colors ${idx === 1 ? 'bg-white font-bold -mb-[1px] border-b-white' : 'bg-gray-200'}`}
            >
              {tab}
            </div>
          ))}
        </div>

        {/* Form Content Area */}
        <div className="bg-white border-x border-b border-gray-400 p-4 flex-1 overflow-auto legacy-scrollbar">
          <fieldset className="border border-gray-300 p-4 pt-2 mb-4">
            <legend className="text-[12px] font-bold text-gray-800 px-2">Address and Contact Information</legend>
            
            <div className="grid grid-cols-4 gap-4">
              <LabelInput label="Address Information" className="col-span-1" />
              <LabelInput label="Previous Addr - Clear (c)" className="col-span-1" />
              <LabelInput label="Mailing Addr - Same (s) or Clear (c)" className="col-span-1" />
              <LabelInput label="Temporary/Secondary Addr" className="col-span-1" />

              <div className="flex flex-col col-span-1">
                <label className="text-[11px] font-sans text-gray-700 mb-0.5">* Permanent Address:</label>
                <textarea className="border border-gray-400 bg-white h-20 p-1 text-[12px] focus:outline-none shadow-inner resize-none" defaultValue="" placeholder="Enter address..." />
              </div>
              <div className="flex flex-col col-span-1">
                <label className="text-[11px] font-sans text-gray-700 mb-0.5">Previous Address:</label>
                <textarea placeholder="Add Address..." className="border border-gray-400 bg-white h-20 p-1 text-[12px] focus:outline-none shadow-inner resize-none" />
              </div>
              <div className="flex flex-col col-span-1">
                <label className="text-[11px] font-sans text-gray-700 mb-0.5">Mailing Address:</label>
                <textarea placeholder="Add Address..." className="border border-gray-400 bg-white h-20 p-1 text-[12px] focus:outline-none shadow-inner resize-none" />
              </div>
              <div className="flex flex-col col-span-1">
                <label className="text-[11px] font-sans text-gray-700 mb-0.5">Temporary/Secondary Address:</label>
                <textarea placeholder="Add Address..." className="border border-gray-400 bg-white h-20 p-1 text-[12px] focus:outline-none shadow-inner resize-none" />
              </div>
            </div>

            <div className="grid grid-cols-6 gap-4 mt-4">
              <LabelInput label="Preferred Phone" defaultValue="" />
              <LabelInput label="Home Phone Number" defaultValue="" />
              <LabelInput label="Mobile Phone Number" defaultValue="" />
              <LabelInput label="Work Phone Number" defaultValue="" />
              <LabelInput label="Work Extension" defaultValue="" />
              <LabelInput label="Alternate Phone Number" defaultValue="" />
            </div>
            <LabelInput label="Email Address" className="max-w-xs mt-2" defaultValue="" />
          </fieldset>

          <fieldset className="border border-gray-300 p-4 pt-2">
            <legend className="text-[12px] font-bold text-gray-800 px-2">Personal Information</legend>
            <div className="grid grid-cols-6 gap-4">
              <LabelInput label="Marital Status" defaultValue="" />
              <LabelInput label="Religion" defaultValue="" />
              <LabelInput label="Language" defaultValue="" />
              <LabelInput label="Interpreter Required" defaultValue="" />
              <LabelInput label="VIP - Person Level" defaultValue="" />
              <LabelInput label="PROMIS" defaultValue="" />
              <LabelInput label="CSBC" defaultValue="" />
            </div>
            <div className="flex flex-col mt-2">
              <label className="text-[11px] font-sans text-gray-700 mb-0.5">Clinic Flag:</label>
              <textarea defaultValue="" placeholder="Enter flag notes..." className="border border-gray-400 bg-white h-12 p-1 text-[12px] focus:outline-none shadow-inner resize-none max-w-md" />
            </div>
          </fieldset>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end space-x-2 mt-4">
          <button className="bg-[#e1e1e1] border-t border-l border-white border-b border-r border-gray-600 px-6 py-1 text-[12px] font-sans active:border-t-gray-600 active:border-l-gray-600 active:border-white active:border-b-white">Complete</button>
          <button className="bg-[#e1e1e1] border-t border-l border-white border-b border-r border-gray-600 px-6 py-1 text-[12px] font-sans active:border-t-gray-600 active:border-l-gray-600 active:border-white active:border-b-white">Cancel</button>
        </div>

        <div className="flex justify-between items-center text-[10px] text-gray-600 mt-2 pt-1 border-t border-gray-300">
          <span>Ready</span>
          <div className="flex space-x-3">
            <span className="border-l border-gray-400 pl-2">TEST.REGCLERK</span>
            <span className="border-l border-gray-400 pl-2">B0783</span>
            <span className="border-l border-gray-400 pl-2">24-Jun-2022 14:27</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegacyRegistration;
